import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, supportedLanguages, defaultLanguage, getTranslation, getInitialLanguage } from '../locales';
import { detectUserLanguage } from '../utils/geoLocation';

const PublicLanguageContext = createContext();

export const usePublicLanguage = () => {
  const context = useContext(PublicLanguageContext);
  if (!context) {
    throw new Error('usePublicLanguage must be used within a PublicLanguageProvider');
  }
  return context;
};

export const PublicLanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Check if user already has a language preference
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage) {
      return savedLanguage;
    }
    // For first-time visitors, we'll detect language based on location
    return getInitialLanguage(); // Use default while detecting
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDetectingLanguage, setIsDetectingLanguage] = useState(false);

  // Get current translations
  const currentTranslations = translations[currentLanguage] || translations[defaultLanguage];

  // Translation function with fallback
  const t = (key, fallback) => {
    return getTranslation(currentTranslations, key, fallback || key);
  };

  // Change language function for public pages only
  const changeLanguage = async (languageCode) => {
    if (!supportedLanguages.some(lang => lang.code === languageCode)) {
      console.warn(`Language ${languageCode} is not supported`);
      return;
    }

    setIsLoading(true);
    
    try {
      // For public pages, always save to localStorage only
      localStorage.setItem('preferredLanguage', languageCode);
      setCurrentLanguage(languageCode);
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Detect language based on location for first-time visitors
  useEffect(() => {
    const detectAndSetLanguage = async () => {
      // Only detect if no language preference is saved
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (!savedLanguage) {
        setIsDetectingLanguage(true);
        try {
          const detectedLanguage = await detectUserLanguage();
          console.log('Detected language based on location:', detectedLanguage);
          
          // Check if detected language is supported
          if (supportedLanguages.some(lang => lang.code === detectedLanguage)) {
            setCurrentLanguage(detectedLanguage);
            // Save the detected language as initial preference
            localStorage.setItem('preferredLanguage', detectedLanguage);
            localStorage.setItem('languageAutoDetected', 'true');
          }
        } catch (error) {
          console.error('Failed to detect language:', error);
          // Keep using the default language
        } finally {
          setIsDetectingLanguage(false);
        }
      }
    };

    detectAndSetLanguage();
  }, []);

  // Listen for localStorage changes (language selector in public header)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'preferredLanguage' && event.newValue) {
        setCurrentLanguage(event.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const value = {
    currentLanguage,
    supportedLanguages,
    t,
    changeLanguage,
    isLoading,
    isDetectingLanguage
  };

  return (
    <PublicLanguageContext.Provider value={value}>
      {children}
    </PublicLanguageContext.Provider>
  );
};

export default PublicLanguageContext;